import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import TextInput from '../../../TextInput';
import Grid from '@mui/material/Grid/Grid';
import { t, translate } from '../../../../../../types/translation/Translator';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { ReorderRuleProduct } from '../../ReorderRuleScreen';
import SearchBarWithFilter from '../../../../../../VentoryUI/components/common/SearchBarWithFilter';
import { ReorderRuleProductFilter } from '../../../../../../VentoryUI/components/filters/ReorderRuleFilter';
import Table, { TableHeader } from '../../../../../components/Table';
import { AddButton, DeleteButton, ExportIconButton, ImportIconButton } from '../../../../../components/Button';
import AddReorderRuleProductModal from './AddReorderRuleProductModal';
import { useRequest } from '../../../../../../requests/common/requestHook';
import moment from 'moment';
import { ReorderRule } from '../../../../../../types/reorderRule';
import { ExportUtil } from '../../../../../../util/export.util';
import { StockTriggerProductRequest } from '../../../../../../requests/stockTriggerRequest';
import { UnitOfMeasure, UnitOfMeasureUtil } from '../../../../../../types/uom';
import { StockLocationAggregateType } from '../../../../../../types/common/stockLocationAggregateType';
import { ProductContext } from '../../../../../../context/ProductContext';
import { ProductTransactionContext } from '../../../../../../context/ProductTransactionContext';
import { ProductTransactionParentType, ProductTransactionType } from '../../../../../../types/productTransaction';
import StockTriggerImportModal from '../../Modals/StockTriggerImportModal';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { Elteros } from '../../../../../../types/customer/Elteros/Elteros';
import { ElterosReorderRuleProductTable } from '../../../../../../types/customer/Elteros/components/ElterosReorderRuleProductTable';
import { OrderContext } from '../../../../../../context/OrderContext';

interface ReorderRuleProductPaneProps {
  reorderRuleProducts: Map<string, ReorderRuleProduct>;
  setReorderRuleProducts: (reorderRuleProducts: Map<string, ReorderRuleProduct>) => void;
  rule: ReorderRule;
  stockLocationIds: string[];
}

export default function ReorderRuleProductPane({
  reorderRuleProducts,
  setReorderRuleProducts,
  rule,
  stockLocationIds,
}: ReorderRuleProductPaneProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { products } = useContext(ProductContext);
  const { orders } = useContext(OrderContext);
  const { productTransactions } = useContext(ProductTransactionContext);

  const allItems = useMemo(() => {
    return [...reorderRuleProducts.values()];
  }, [reorderRuleProducts]);

  const [items, setItems] = useState<ReorderRuleProduct[]>([...allItems]);
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [exportProducts, { loading }] = useRequest(StockTriggerProductRequest.download);

  useEffect(() => {
    setItems([...reorderRuleProducts.values()]);
  }, [reorderRuleProducts]);

  const handleDownload = async () => {
    const file = await exportProducts({
      ids: [rule.groupId],
      timezone: moment.tz.guess(),
      locale: (window.navigator as any)['userLanguage'] || window.navigator.language,
    });
    if (!file) return;

    ExportUtil.downloadExport(file);
  };

  const headers = () => {
    const items: TableHeader<ReorderRuleProduct>[] = [
      {
        key: 'name',
        name: translate({ key: TranslationKey.productName }),
        weight: 4,
        text: (item: ReorderRuleProduct) => productMasterData.get(item.productMasterDataId || '')?.productName || '',
      },
      {
        key: 'number',
        name: translate({ key: TranslationKey.productNumber }),
        weight: 4,
        text: (item: ReorderRuleProduct) => productMasterData.get(item.productMasterDataId || '')?.productNumber || '',
      },
    ];

    const allOrAllOf =
      rule.stockLocationAggregateType === StockLocationAggregateType.all ||
      rule.stockLocationAggregateType === StockLocationAggregateType.allOf;
    if (allOrAllOf) {
      items.push({
        key: 'in_stock',
        name: 'In Stock',
        weight: 2,
        text: (item: ReorderRuleProduct) => {
          if (currentCompany.id === Elteros.companyId && rule.groupId === Elteros.perpardavimųSTOCKReorderRuleId) {
            return ElterosReorderRuleProductTable.inStockItem(
              products,
              productMasterData,
              stockLocationIds,
              rule,
              item,
            );
          }

          let inStock = [...(products.get(item.productMasterDataId || '')?.values() || [])];
          if (rule.stockLocationAggregateType === StockLocationAggregateType.allOf) {
            inStock = inStock.filter(p => stockLocationIds.includes(p.stockLocationId));
          }
          const pmd = productMasterData.get(item.productMasterDataId || '');
          const quantity = inStock.reduce((sum, i) => sum + BigInt(i.unitQuantity), BigInt(0));
          return UnitOfMeasure.toQuantityWithSuffix(pmd?.unitOfMeasure, quantity.toString());
        },
      });

      items.push({
        key: 'incoming',
        name: 'Incoming',
        weight: 2,
        text: (item: ReorderRuleProduct) => {
          if (currentCompany.id === Elteros.companyId && rule.groupId === Elteros.perpardavimųSTOCKReorderRuleId) {
            return ElterosReorderRuleProductTable.incomingItem(
              productTransactions,
              productMasterData,
              orders,
              stockLocationIds,
              rule,
              item,
            );
          }

          let incoming = [...productTransactions.values()].filter(
            t =>
              t.type === ProductTransactionType.inbound &&
              t.parentType === ProductTransactionParentType.order &&
              t.product.pmdId === item.productMasterDataId,
          );
          if (rule.stockLocationAggregateType === StockLocationAggregateType.allOf) {
            incoming = incoming.filter(t => stockLocationIds.includes(t.product.toStockLocationId || ''));
          }
          const pmd = productMasterData.get(item.productMasterDataId || '');
          const quantity = incoming.reduce((sum, t) => sum + t.product.quantity, 0);
          return UnitOfMeasure.quantityWithSuffix(pmd?.unitOfMeasure, quantity.toString());
        },
      });
    }

    items.push({
      key: 'reorderQuantity',
      name: 'Reorder Quantity',
      weight: 2,
      text: (item: ReorderRuleProduct) => (
        <Grid container className='h-7 w-full' justifyContent={'flex-start'}>
          <Grid item xs={7} marginY={'auto'} justifyContent={'flex-start'}>
            <TextInput
              type='number'
              value={item.reorderQuantity.toString()}
              heightClass='h-7'
              dynamicUpdate
              min={0}
              onChange={v => {
                const foundItem = reorderRuleProducts.get(item.productMasterDataId || '');
                if (!foundItem) return;
                foundItem.reorderQuantity = v;
              }}
              suffix={
                UnitOfMeasureUtil.getSuffix(productMasterData.get(item.productMasterDataId || '')?.unitOfMeasure) || ' '
              }
            />
          </Grid>
        </Grid>
      ),
    });

    items.push({
      key: 'replenishQuantity',
      name: 'Replenish Quantity',
      weight: 2,
      text: (item: ReorderRuleProduct) => (
        <Grid container className='h-7' justifyContent={'flex-end'}>
          <Grid item xs={7} marginY={'auto'} justifyContent={'flex-end'} mr={0.1}>
            <TextInput
              type='number'
              value={item.replenishQuantity.toString()}
              heightClass='h-7'
              dynamicUpdate
              min={0}
              onChange={v => {
                const foundItem = reorderRuleProducts.get(item.productMasterDataId || '');
                if (!foundItem) return;
                foundItem.replenishQuantity = v;
              }}
              suffix={
                UnitOfMeasureUtil.getSuffix(productMasterData.get(item.productMasterDataId || '')?.unitOfMeasure) || ' '
              }
            />
          </Grid>
        </Grid>
      ),
    });

    return items;
  };

  const filter = new ReorderRuleProductFilter(productMasterData);

  return (
    <>
      {addModalOpen && (
        <AddReorderRuleProductModal
          open={addModalOpen}
          setOpen={setAddModalOpen}
          reorderRuleProducts={reorderRuleProducts}
          setReorderRuleProducts={setReorderRuleProducts}
        />
      )}

      {importModalOpen && (
        <StockTriggerImportModal
          open={importModalOpen}
          setOpen={setImportModalOpen}
          reorderRuleProducts={reorderRuleProducts}
          setReorderRuleProducts={setReorderRuleProducts}
        />
      )}

      <div className='flex h-full flex-col gap-4'>
        <div className='flex justify-between gap-4'>
          <SearchBarWithFilter
            items={allItems}
            setItems={setItems}
            placeholder={t(TranslationKey.filterProducts).singular.label}
            filter={filter}
          />
          <div className='flex gap-2'>
            {selected.size ? (
              <DeleteButton
                onClick={() => {
                  selected.forEach(id => reorderRuleProducts.delete(id));
                  setReorderRuleProducts(new Map(reorderRuleProducts));
                  setSelected(new Set());
                }}
              />
            ) : null}
            {allItems.length && rule.version ? (
              <ExportIconButton loading={loading} onClick={() => handleDownload()} />
            ) : null}
            <ImportIconButton onClick={() => setImportModalOpen(true)} />
            <AddButton onClick={() => setAddModalOpen(true)} />
          </div>
        </div>
        <Table
          uniqueIdentifier='productMasterDataId'
          loading={productMasterDataLoading}
          selectedValues={selected}
          onSelected={items => {
            setSelected(items);
          }}
          items={items}
          totalItemCount={allItems.length}
          headers={headers()}
          filter={filter}
        />
      </div>
    </>
  );
}
